import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { HttpMethod, callApi } from "src/services/apiService";

interface InvoiceData {
  invoiceId: string;
  invoiceNum: string;
  amount: number;
  sentOn: Date;
  // Credit-card surcharge percent applied at payment time (per-account; 2% default).
  surchargePercent: number;
  cardOnFile: {
    cardId?: string;
    cardType: string;
    number: string;
    cardholderName: string;
    expiration: string;
  };
}

interface PaymentFormData {
  cardNumber: string;
  cardholderName: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  phone: string;
}

function PayInvoiceOnline() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [error, setError] = useState<string>("");
  const [cardType, setCardType] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<"saved" | "new">("new");

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
  } = useForm<PaymentFormData>({
    mode: "onChange",
    defaultValues: {
      cardNumber: "",
      cardholderName: "",
      expiryMonth: "",
      expiryYear: "",
      cvv: "",
      street: "",
      city: "",
      state: "",
      zipCode: "",
      email: "",
      phone: "",
    },
  });

  const watchedCardNumber = watch("cardNumber");

  // Currency formatter shared across the page.
  const usdFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });
  const formatUsd = (value: number) => usdFormatter.format(value);

  // Surcharge breakdown disclosed to the customer. The server recomputes and is
  // authoritative, but we mirror its rounding (2 decimals, half-up) so the displayed
  // total matches what the card will be charged.
  const surchargePercent = invoice?.surchargePercent ?? 0;
  const surchargeFee = invoice
    ? Math.round(invoice.amount * surchargePercent) / 100
    : 0;
  const totalToCharge = invoice ? invoice.amount + surchargeFee : 0;

  // Check if form is valid based on payment method
  const isFormValidForPaymentMethod = () => {
    if (paymentMethod === "saved") {
      return true; // No validation needed for saved payment method
    }

    // For new card, check if all required fields are valid
    const requiredFields = [
      "email",
      "cardNumber",
      "cardholderName",
      "expiryMonth",
      "expiryYear",
      "cvv",
      "street",
      "city",
      "state",
      "zipCode",
    ];

    return requiredFields.every(
      (field) => !errors[field as keyof PaymentFormData],
    );
  };

  // Fetch invoice data
  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceData();
    }
  }, [invoiceId]);

  // Detect card type based on card number
  useEffect(() => {
    const number = watchedCardNumber.replace(/\s/g, "");
    if (number.startsWith("4")) {
      setCardType("visa");
    } else if (number.startsWith("5")) {
      setCardType("mastercard");
    } else if (number.startsWith("3")) {
      setCardType("amex");
    } else if (number.startsWith("6")) {
      setCardType("discover");
    } else {
      setCardType("");
    }
  }, [watchedCardNumber]);

  // Trigger validation when payment method changes
  useEffect(() => {
    if (paymentMethod === "new") {
      // Trigger validation for all fields when switching to new card
      trigger();
    }
  }, [paymentMethod, trigger]);

  const fetchInvoiceData = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await callApi(
        `payment?invoiceId=${invoiceId}`,
        HttpMethod.GET,
      );

      if (response?.type === "FAILED") {
        setError(response.message || "Failed to load invoice");
        return;
      }

      if (response) {
        setInvoice(response);
        // Set payment method based on whether there's a saved card
        setPaymentMethod(response.cardOnFile ? "saved" : "new");
      }
    } catch (error: any) {
      console.error("Error fetching invoice:", error);
      setError("Failed to load invoice. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(" ");
    } else {
      return v;
    }
  };

  // Validate card number using Luhn algorithm
  const validateCardNumber = (value: string) => {
    const number = value.replace(/\s/g, "");
    if (number.length < 13 || number.length > 19) {
      return "Card number must be between 13 and 19 digits";
    }

    // Luhn algorithm
    let sum = 0;
    let isEven = false;
    for (let i = number.length - 1; i >= 0; i--) {
      let digit = parseInt(number.charAt(i));
      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      sum += digit;
      isEven = !isEven;
    }
    return sum % 10 === 0 || "Invalid card number";
  };

  // Validate expiry date
  // Validate CVV
  const validateCVV = (value: string) => {
    const cvv = value.replace(/\s/g, "");
    const isAmex = cardType === "amex";
    const expectedLength = isAmex ? 4 : 3;

    if (cvv.length !== expectedLength) {
      return `CVV must be ${expectedLength} digits`;
    }

    return true;
  };

  const handlePaymentSubmit = async (data: PaymentFormData) => {
    // Guard against a double charge: once a payment has gone through, never resubmit.
    if (isPaid || isProcessing) return;

    setIsProcessing(true);
    setError("");

    try {
      const paymentUrl = `payment?invoiceId=${invoiceId}&amount=${invoice?.amount}&useCardOnFile=${paymentMethod === "saved"}`;
      const cardInfo =
        paymentMethod === "saved"
          ? undefined
          : {
              number: data.cardNumber.replace(/\s/g, ""),
              cardholderName: data.cardholderName,
              expiration: `${data.expiryMonth}${data.expiryYear}`,
              cvv: data.cvv,
              street: data.street,
              city: data.city,
              region: data.state,
              postCode: data.zipCode,
              email: data.email,
              phone: data.phone,
            };

      // Submit payment
      const response = await callApi(paymentUrl, HttpMethod.POST, cardInfo);

      if (response?.type === "FAILED") {
        setError(response.message || "Payment failed. Please try again.");
        return;
      }

      if (response?.success) {
        // Payment successful. The server returns the authoritative amount charged
        // (invoice amount + surcharge); confirm it back to the customer.
        const charged =
          typeof response.amountCharged === "number"
            ? formatUsd(response.amountCharged)
            : formatUsd(totalToCharge);
        // Latch paid state so the form can't be resubmitted (prevents a double charge).
        setIsPaid(true);
        alert(`Payment of ${charged} processed successfully!`);

        //todo: close popup -------------------------------------------------------------
      } else {
        setError(
          "Payment failed. Please check your card details and try again.",
        );
      }
    } catch (error: any) {
      console.error("Payment error:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear + i);
  const months = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return month.toString().padStart(2, "0");
  });

  if (isLoading) {
    return (
      <div className="p-3 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading invoice details...</p>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          <h5>Unable to load invoice</h5>
          <p>{error}</p>
          <button className="btn btn-outline-danger" onClick={fetchInvoiceData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row justify-content-center">
        <div className="col">
          <div className="card">
            <div className="card-header">
              <h1 className="h3 mb-0">Pay Invoice: {invoice?.invoiceNum}</h1>
            </div>
            <div className="card-body">
              {invoice && (
                <div className="row mb-4">
                  <div className="col">
                    <h5>Invoice Details</h5>
                    <div className="table-responsive">
                      <table className="table table-borderless">
                        <tbody>
                          <tr>
                            <td>
                              <strong>Amount:</strong>
                            </td>
                            <td>
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                              }).format(invoice.amount)}
                            </td>
                          </tr>

                          <tr>
                            <td>
                              <strong>Sent On:</strong>
                            </td>
                            <td>
                              {new Date(invoice.sentOn).toLocaleDateString()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-center p-3 bg-light rounded">
                      <h4>Total to Pay</h4>
                      <div className="h2 text-primary">
                        {formatUsd(totalToCharge)}
                      </div>
                      {surchargeFee > 0 && (
                        <small className="text-muted">
                          Includes a {surchargePercent}% credit card processing
                          fee of {formatUsd(surchargeFee)}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Select a payment method - only show if there's a saved card */}
              {invoice?.cardOnFile && (
                <div className="card mb-4">
                  <div className="card-header">
                    <h5>Select a payment method</h5>
                  </div>
                  <div className="card-body">
                    {/* Your saved form of payment */}
                    <div className="mb-4">
                      <h6>Your saved form of payment</h6>
                      <div className="form-check">
                        <input
                          type="radio"
                          id="savedPayment"
                          name="paymentMethod"
                          className="form-check-input"
                          checked={paymentMethod === "saved"}
                          onChange={() => setPaymentMethod("saved")}
                          disabled={isProcessing}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="savedPayment"
                        >
                          Use saved payment method
                        </label>
                      </div>
                      <div className="mt-2 ms-4 p-3 bg-light rounded">
                        <div className="row">
                          <div className="col-md-6">
                            <strong>Card:</strong> •••• •••• ••••{" "}
                            {invoice.cardOnFile.number.slice(-4)}
                          </div>
                          <div className="col-md-6">
                            <strong>Type:</strong> {invoice.cardOnFile.cardType}
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-md-6">
                            <strong>Expires:</strong>{" "}
                            {invoice.cardOnFile.expiration}
                          </div>
                          <div className="col-md-6">
                            <strong>Name:</strong>{" "}
                            {invoice.cardOnFile.cardholderName}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* A different credit/debit card */}
                    <div>
                      <h6>A different credit/debit card</h6>
                      <div className="form-check">
                        <input
                          type="radio"
                          id="newCard"
                          name="paymentMethod"
                          className="form-check-input"
                          checked={paymentMethod === "new"}
                          onChange={() => setPaymentMethod("new")}
                          disabled={isProcessing}
                        />
                        <label className="form-check-label" htmlFor="newCard">
                          Enter new card details
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger">
                  <h6>Payment Error</h6>
                  <p className="mb-0">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit(handlePaymentSubmit)}>
                <div className="row">
                  <div className="col-lg-8">
                    {/* Contact Information */}
                    {(!invoice?.cardOnFile || paymentMethod === "new") && (
                      <div className="card mb-4">
                        <div className="card-header">
                          <h5>Contact Information</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group mb-3">
                                <label htmlFor="email" className="form-label">
                                  Email Address *
                                </label>
                                <Controller
                                  name="email"
                                  control={control}
                                  rules={{
                                    required: "Email is required",
                                    pattern: {
                                      value:
                                        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                      message: "Invalid email address",
                                    },
                                  }}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="email"
                                      id="email"
                                      className={`form-control ${
                                        errors.email ? "is-invalid" : ""
                                      }`}
                                      placeholder="your@email.com"
                                      disabled={isProcessing}
                                    />
                                  )}
                                />
                                {errors.email && (
                                  <div className="invalid-feedback">
                                    {errors.email.message}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group mb-3">
                                <label htmlFor="phone" className="form-label">
                                  Phone Number
                                </label>
                                <Controller
                                  name="phone"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="tel"
                                      id="phone"
                                      className="form-control"
                                      placeholder="(555) 123-4567"
                                      disabled={isProcessing}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Credit Card Information */}
                    {(!invoice?.cardOnFile || paymentMethod === "new") && (
                      <div className="card mb-4">
                        <div className="card-header">
                          <h5>Credit Card Information</h5>
                        </div>
                        <div className="card-body">
                          {/* Card Number */}
                          <div className="form-group mb-3">
                            <label htmlFor="cardNumber" className="form-label">
                              Card Number *
                            </label>
                            <div className="position-relative">
                              <Controller
                                name="cardNumber"
                                control={control}
                                rules={{
                                  required: "Card number is required",
                                  validate: validateCardNumber,
                                }}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="text"
                                    id="cardNumber"
                                    className={`form-control ${
                                      errors.cardNumber ? "is-invalid" : ""
                                    }`}
                                    placeholder="1234 5678 9012 3456"
                                    maxLength={19}
                                    onChange={(e) => {
                                      field.onChange(
                                        formatCardNumber(e.target.value),
                                      );
                                    }}
                                    disabled={isProcessing}
                                  />
                                )}
                              />
                              {cardType && (
                                <div className="position-absolute top-50 end-0 translate-middle-y pe-3">
                                  <img
                                    src={`/images/cards/${cardType}.svg`}
                                    alt={cardType}
                                    style={{ width: "32px", height: "20px" }}
                                  />
                                </div>
                              )}
                            </div>
                            {errors.cardNumber && (
                              <div className="invalid-feedback">
                                {errors.cardNumber.message}
                              </div>
                            )}
                          </div>

                          {/* Cardholder Name */}
                          <div className="form-group mb-3">
                            <label
                              htmlFor="cardholderName"
                              className="form-label"
                            >
                              Cardholder Name *
                            </label>
                            <Controller
                              name="cardholderName"
                              control={control}
                              rules={{
                                required: "Cardholder name is required",
                                minLength: {
                                  value: 2,
                                  message: "Name must be at least 2 characters",
                                },
                              }}
                              render={({ field }) => (
                                <input
                                  {...field}
                                  type="text"
                                  id="cardholderName"
                                  className={`form-control ${
                                    errors.cardholderName ? "is-invalid" : ""
                                  }`}
                                  placeholder="John Doe"
                                  disabled={isProcessing}
                                />
                              )}
                            />
                            {errors.cardholderName && (
                              <div className="invalid-feedback">
                                {errors.cardholderName.message}
                              </div>
                            )}
                          </div>

                          <div className="row">
                            {/* Expiry Date */}
                            <div className="col-md-4">
                              <div className="form-group mb-3">
                                <label
                                  htmlFor="expiryMonth"
                                  className="form-label"
                                >
                                  Expiry Month *
                                </label>
                                <Controller
                                  name="expiryMonth"
                                  control={control}
                                  rules={{
                                    required: "Expiry month is required",
                                  }}
                                  render={({ field }) => (
                                    <select
                                      {...field}
                                      className={`form-select ${
                                        errors.expiryMonth ? "is-invalid" : ""
                                      }`}
                                      disabled={isProcessing}
                                    >
                                      <option value="">Month</option>
                                      {months.map((month) => (
                                        <option key={month} value={month}>
                                          {month}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                />
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="form-group mb-3">
                                <label
                                  htmlFor="expiryYear"
                                  className="form-label"
                                >
                                  Expiry Year *
                                </label>
                                <Controller
                                  name="expiryYear"
                                  control={control}
                                  rules={{
                                    required: "Expiry year is required",
                                  }}
                                  render={({ field }) => (
                                    <select
                                      {...field}
                                      className={`form-select ${
                                        errors.expiryYear ? "is-invalid" : ""
                                      }`}
                                      disabled={isProcessing}
                                    >
                                      <option value="">Year</option>
                                      {years.map((year) => (
                                        <option
                                          key={year}
                                          value={year.toString().slice(-2)}
                                        >
                                          {year}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                />
                              </div>
                            </div>

                            {/* CVV */}
                            <div className="col-md-4">
                              <div className="form-group mb-3">
                                <label htmlFor="cvv" className="form-label">
                                  CVV *
                                </label>
                                <Controller
                                  name="cvv"
                                  control={control}
                                  rules={{
                                    required: "CVV is required",
                                    validate: validateCVV,
                                  }}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="text"
                                      id="cvv"
                                      className={`form-control ${
                                        errors.cvv ? "is-invalid" : ""
                                      }`}
                                      placeholder={
                                        cardType === "amex" ? "1234" : "123"
                                      }
                                      maxLength={cardType === "amex" ? 4 : 3}
                                      onChange={(e) => {
                                        field.onChange(
                                          e.target.value.replace(/\D/g, ""),
                                        );
                                      }}
                                      disabled={isProcessing}
                                    />
                                  )}
                                />
                                {errors.cvv && (
                                  <div className="invalid-feedback">
                                    {errors.cvv.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Billing Address */}
                    {(!invoice?.cardOnFile || paymentMethod === "new") && (
                      <div className="card mb-4">
                        <div className="card-header">
                          <h5>Billing Address</h5>
                        </div>
                        <div className="card-body">
                          <div className="form-group mb-3">
                            <label htmlFor="address" className="form-label">
                              Street Address *
                            </label>
                            <Controller
                              name="street"
                              control={control}
                              rules={{
                                required: "Street address is required",
                              }}
                              render={({ field }) => (
                                <input
                                  {...field}
                                  type="text"
                                  id="address"
                                  className={`form-control ${
                                    errors.street ? "is-invalid" : ""
                                  }`}
                                  placeholder="123 Main St"
                                  disabled={isProcessing}
                                />
                              )}
                            />
                            {errors.street && (
                              <div className="invalid-feedback">
                                {errors.street.message}
                              </div>
                            )}
                          </div>

                          <div className="row">
                            <div className="col-md-4">
                              <div className="form-group mb-3">
                                <label htmlFor="city" className="form-label">
                                  City *
                                </label>
                                <Controller
                                  name="city"
                                  control={control}
                                  rules={{
                                    required: "City is required",
                                  }}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="text"
                                      id="city"
                                      className={`form-control ${
                                        errors.city ? "is-invalid" : ""
                                      }`}
                                      placeholder="New York"
                                      disabled={isProcessing}
                                    />
                                  )}
                                />
                                {errors.city && (
                                  <div className="invalid-feedback">
                                    {errors.city.message}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="form-group mb-3">
                                <label htmlFor="state" className="form-label">
                                  State *
                                </label>
                                <Controller
                                  name="state"
                                  control={control}
                                  rules={{
                                    required: "State is required",
                                  }}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="text"
                                      id="state"
                                      className={`form-control ${
                                        errors.state ? "is-invalid" : ""
                                      }`}
                                      placeholder="NY"
                                      maxLength={2}
                                      disabled={isProcessing}
                                    />
                                  )}
                                />
                                {errors.state && (
                                  <div className="invalid-feedback">
                                    {errors.state.message}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="form-group mb-3">
                                <label htmlFor="zipCode" className="form-label">
                                  ZIP Code *
                                </label>
                                <Controller
                                  name="zipCode"
                                  control={control}
                                  rules={{
                                    required: "ZIP code is required",
                                    pattern: {
                                      value: /^\d{5}(-\d{4})?$/,
                                      message: "Please enter a valid ZIP code",
                                    },
                                  }}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      type="text"
                                      id="zipCode"
                                      className={`form-control ${
                                        errors.zipCode ? "is-invalid" : ""
                                      }`}
                                      placeholder="12345"
                                      disabled={isProcessing}
                                    />
                                  )}
                                />
                                {errors.zipCode && (
                                  <div className="invalid-feedback">
                                    {errors.zipCode.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-lg-4">
                    {/* Payment Summary */}
                    <div className="card sticky-top" style={{ top: "1rem" }}>
                      <div className="card-header">
                        <h5>Payment Summary</h5>
                      </div>
                      <div className="card-body">
                        {invoice && (
                          <>
                            <div className="d-flex justify-content-between mb-2">
                              <span>Invoice Amount:</span>
                              <span>{formatUsd(invoice.amount)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <span>
                                Credit Card Processing Fee
                                {surchargePercent > 0
                                  ? ` (${surchargePercent}%)`
                                  : ""}
                                :
                              </span>
                              <span>{formatUsd(surchargeFee)}</span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between mb-3">
                              <strong>Total:</strong>
                              <strong>{formatUsd(totalToCharge)}</strong>
                            </div>
                          </>
                        )}

                        <button
                          type="submit"
                          className={`btn w-100 ${
                            isPaid ? "btn-success" : "btn-primary"
                          }`}
                          disabled={
                            isPaid ||
                            isProcessing ||
                            !isFormValidForPaymentMethod()
                          }
                        >
                          {isPaid ? (
                            <>
                              <i className="bi bi-check-circle me-2" />
                              Payment Complete
                            </>
                          ) : isProcessing ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Processing Payment...
                            </>
                          ) : (
                            `Pay ${invoice ? formatUsd(totalToCharge) : ""}`
                          )}
                        </button>

                        {isPaid && (
                          <div className="alert alert-success mt-3 mb-0 text-center">
                            Your payment has been processed. This invoice is paid
                            in full.
                          </div>
                        )}

                        <div className="mt-3 text-center">
                          <small className="text-muted">
                            Your payment is secure and encrypted
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PayInvoiceOnline;
